import compact from 'lodash.compact';
export const getOrgReference = (newKeys, values, orgTree) => {
    // This navigates deeper and deeper into the org tree until it finds the
    // correct reference
    return newKeys.reduce((acc, curr) => {
        // For each key in `newKeys`, check if the current accumulator (`acc`) has a property matching the key's `id`.
        if (acc?.[curr.id]) {
            // If a match is found, update the accumulator to the item in the array that matches the value of the current key's `id` in the `values` object.
            return acc?.[curr.id]?.find?.((item) => {
                return item.id === values[curr.id];
            });
        }
    }, orgTree); // Start the reduction with the root of the organizational tree (`orgTree`).
};
export const departmentListToString = (company, department) => {
    // This function converts a list of department IDs into a string representation of the department path.
    // It uses the company's organizational tree structure to find the names of the departments.
    const rawPath = company?.orgTreeNextLevelKeys?.reduce((acc, curr, index, keys) => {
        // Skip the first iteration and initialize the finalValue array.
        if (index === 0) {
            return {
                ...acc,
                id: company.id,
                name: company.name,
                finalValue: [],
            };
        }
        // Find the current department in the organizational tree.
        const orgTree = acc[curr.id]?.find?.((org) => {
            return org.id === department?.[index];
        });
        // If the department is not found, return the accumulated path or the current name.
        if (!orgTree) {
            if (acc.finalValue) {
                return [...acc.finalValue, acc.name];
            }
            else {
                return acc;
            }
        }
        // If this is the last key, add the current name and the found department name to the path.
        if (index === keys.length - 1) {
            return [...acc.finalValue, acc.name, orgTree?.name];
        }
        // Continue accumulating the path.
        return {
            ...orgTree,
            finalValue: [...acc.finalValue, acc.name],
        };
    }, { ...company.orgTree, finalValue: [] });
    if (compact(rawPath).length !== department.length) {
        return 'Invalid Department';
    }
    else {
        return rawPath.join(' > ');
    }
};
export const nearestValidDepartmentList = (company, department) => {
    const rawPath = company?.orgTreeNextLevelKeys?.reduce((acc, curr, index, keys) => {
        if (index === 0) {
            return {
                ...acc,
                finalValue: [],
            };
        }
        const orgTree = acc[curr.id]?.find?.((org) => {
            return org.id === department?.[index];
        });
        if (index === keys.length - 1) {
            return [...acc.finalValue, acc.name, orgTree?.id];
        }
        return {
            ...orgTree,
            finalValue: [...acc.finalValue, acc.name],
        };
    }, { ...company, finalValue: [] });
    return compact(rawPath).join(' > ');
};
export const departmentStringToFriendlyString = (company, department) => {
    const departmentArray = department.split('.');
    return departmentListToString(company, departmentArray);
};
export const getCommonParentDepartment = (departments = [], lastCommonParentMatch = '', index = 0) => {
    const getAllDepartmentsUpToIndex = (department, index) => {
        return department
            .split('.')
            .slice(0, index + 1)
            .join('.');
    };
    const departmentsForIndex = departments.map((department) => getAllDepartmentsUpToIndex(department, index));
    const isEveryDepartmentSame = departmentsForIndex.every((department) => department === departmentsForIndex[0]);
    if (departments.length === 0 || !isEveryDepartmentSame) {
        return lastCommonParentMatch;
    }
    if (isEveryDepartmentSame && departments.length === 1) {
        return departments[0];
    }
    if (isEveryDepartmentSame) {
        return getCommonParentDepartment(departments, departmentsForIndex[0], index + 1);
    }
    return lastCommonParentMatch;
};
export const employeeIsPartOfOrganization = (employee, orgPath) => {
    const department = employee.department.join('.');
    return department.startsWith(orgPath);
};
export const getEmployeesMappedByDepartment = (employees, departments) => {
    return departments.reduce((acc, department) => {
        acc[department] = employees.filter((employee) => employeeIsPartOfOrganization(employee, department));
        return acc;
    }, {});
};
export const getOrgTreeFromPath = (company, path) => {
    const pathArray = path.split('.');
    return {
        orgTree: pathArray.reduce((acc, curr, index, array) => {
            if (index === 0) {
                return acc;
            }
            const key = company?.orgTreeNextLevelKeys?.[index].id || '';
            return acc[key]?.find?.((item) => {
                return item.id === curr;
            });
        }, company),
        remainingNextLevelKeys: (company?.orgTreeNextLevelKeys || []).slice(pathArray.length),
    };
};
export const getAllChildDepartments = (orgTree, path, nextLevelKeys) => {
    const nextLevelKey = nextLevelKeys[0];
    if (!nextLevelKey) {
        return [path];
    }
    const childDepartments = orgTree?.[nextLevelKey.id] || [];
    if (childDepartments.length === 0) {
        return [path];
    }
    const paths = [];
    for (let i = 0; i < childDepartments.length; i++) {
        if (childDepartments[i]) {
            paths.push(getAllChildDepartments(childDepartments[i], path + '.' + childDepartments[i].id, nextLevelKeys.slice(1)));
        }
    }
    return paths;
};
